import React, { useCallback, useEffect, useMemo } from "react";

import type {
  ColDef,
  ColGroupDef,
  GetRowIdParams,
  GridReadyEvent,
} from "ag-grid-community";
import { Grid } from "@material-ui/core";
import { Formik, useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import {
  InterestPreviewAction,
  type InterestPreviewRequest,
} from "actions/interestPreview";

import AgGridTable from "components/AgGrid/AgGridContainer";
import { InputDateRange } from "components/DateRangePicker/dateRangePicker";
import { Button, SecondaryButton } from "components/Form";
import { OptionTogglerField } from "components/Form/FormikControls";
import { Column } from "components/Layout";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import {
  BillingDocumentColId,
  BillingDocumentColumnDef,
  BusinessColumnDef,
  InterestPreviewColId,
  InterestPreviewColumnDef,
} from "constants/columnDefinitions";
import { AgGridTables } from "constants/aggrid";
import { CreditDebit } from "constants/interest";
import { ModalTypes } from "constants/navigation";

import { filterColIdsFromColumnDef } from "lib/agGrid";
import { pluralize } from "lib/pluralize";
import { formatISO8601DateString } from "lib/timeFormats";

import type { InterestPreviewAggridRow } from "selectors/aggrid/interestPreview";

import {
  getContextByModalType,
  getIsCreatingInterestCharges,
  getIsStaff,
  getProperties,
  getIsFetchingInterestPreview,
  selectInterestPreviewAgGridData,
} from "selectors";

interface InterestPreviewProps {
  onClose: () => void;
  businessIds?: string[];
}

interface InterestPreviewModalContext {
  businessIds?: string[];
}

interface InterestPreviewFormValues {
  chargeFromDate: string | null;
  chargeToDate: string;
  creditDebit: NonNullable<InterestPreviewRequest["creditDebit"]>;
}

const DOCUMENT_TYPE_OPTIONS = [
  { value: CreditDebit.DEBIT, label: "Invoice" },
  { value: CreditDebit.CREDIT, label: "RCTI" },
  { value: CreditDebit.BOTH, label: "Invoice and RCTI" },
];

const DATE_RANGE_TOOLTIP =
  "Interest is calculated over the selected date range, inclusive of the charge-to date.";

const getInterestPreviewRowId = (
  params: GetRowIdParams<InterestPreviewAggridRow>,
) => params.data.interestPreview.id;

const billingDocumentColumns = filterColIdsFromColumnDef(
  BillingDocumentColumnDef,
  [BillingDocumentColId.VENDOR_NUMBER, BillingDocumentColId.VENDOR_COMMISSION],
);

const columnDefs: (ColDef | ColGroupDef)[] = [
  {
    headerName: "Business",
    children: Object.values(BusinessColumnDef) as ColDef[],
  },
  {
    headerName: "Billing Document",
    children: billingDocumentColumns as ColDef[],
  },
  {
    headerName: "Interest Preview",
    children: Object.values(InterestPreviewColumnDef) as ColDef[],
  },
];

// Added after people had already saved views of this table, which would
// otherwise hide them. Warnings is the only place a skipped business says why.
const ALWAYS_VISIBLE_COL_IDS = [
  InterestPreviewColId.CALCULATION_BASIS,
  InterestPreviewColId.WARNINGS,
];

const validationSchema = Yup.object().shape({
  chargeFromDate: Yup.string()
    .nullable()
    .test(
      "valid-date-range",
      "Charge From must not be after Charge To",
      function isValidDateRange(chargeFromDate) {
        return (
          !chargeFromDate ||
          !this.parent.chargeToDate ||
          chargeFromDate <= this.parent.chargeToDate
        );
      },
    ),
  chargeToDate: Yup.string().required("Charge To is required"),
});

function InterestPreviewModal({ onClose, businessIds }: InterestPreviewProps) {
  const dispatch = useDispatch();
  const isStaff = useSelector(getIsStaff);
  const propertyByIdLookup = useSelector(getProperties);
  const rowData = useSelector(selectInterestPreviewAgGridData);
  const isLoading = useSelector(getIsFetchingInterestPreview);
  const isCreating = useSelector(getIsCreatingInterestCharges);

  const { handleSubmit, isValid, setValues, values } =
    useFormikContext<InterestPreviewFormValues>();
  const { chargeFromDate, chargeToDate, creditDebit } = values;

  const context = useMemo(() => ({ propertyByIdLookup }), [propertyByIdLookup]);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    const updateGrandTotalRow = () =>
      event.api.setGridOption(
        "grandTotalRow",
        event.api.getValueColumns().length > 0 ? "bottom" : undefined,
      );
    updateGrandTotalRow();
    event.api.addEventListener("columnValueChanged", updateGrandTotalRow);
  }, []);

  useEffect(() => {
    if (!chargeToDate || !isValid) {
      dispatch(InterestPreviewAction.reset());
      return;
    }

    dispatch(
      InterestPreviewAction.request({
        chargeToDate,
        chargeFromDate: chargeFromDate || null,
        creditDebit,
        businessIds,
      }),
    );

    return () => {
      dispatch(InterestPreviewAction.reset());
    };
  }, [
    chargeFromDate,
    chargeToDate,
    creditDebit,
    businessIds,
    dispatch,
    isValid,
  ]);

  const updateDates = (
    nextChargeFromDate: string | null,
    nextChargeToDate: string | null,
  ) => {
    setValues({
      ...values,
      chargeFromDate: nextChargeFromDate,
      chargeToDate: nextChargeToDate ?? "",
    });
  };

  if (!isStaff) return null;

  return (
    <Dialog
      fullScreen
      open
      onClose={onClose}
      PaperProps={{ component: "form", onSubmit: handleSubmit }}
    >
      <DialogTitle onClose={onClose}>
        {businessIds?.length
          ? `Preview Interest Charges for ${businessIds.length} ${pluralize("Business", businessIds.length)}`
          : "Preview Interest Charges"}
      </DialogTitle>
      <DialogContent dividers>
        <Column fullWidth fullHeight gridGap={2}>
          <Grid container spacing={2}>
            <InputDateRange
              updateDates={updateDates}
              startDateFieldName="chargeFromDate"
              endDateFieldName="chargeToDate"
              includeTime={false}
              tooltip={DATE_RANGE_TOOLTIP}
              label="Date Range"
              startDateLabel="Charge From"
              endDateLabel="Charge To"
            />
            <Grid item xs={12}>
              <OptionTogglerField
                name="creditDebit"
                label="Document Type"
                labelPosition="top"
                options={DOCUMENT_TYPE_OPTIONS}
              />
            </Grid>
          </Grid>

          {chargeToDate && (
            <AgGridTable
              rowData={rowData}
              columnDefs={columnDefs}
              loading={isLoading}
              context={context}
              tableName={AgGridTables.INTEREST_PREVIEW}
              onGridReady={onGridReady}
              alwaysVisibleColIds={ALWAYS_VISIBLE_COL_IDS}
              hideHeader
              getRowId={getInterestPreviewRowId}
              pagination={false}
            />
          )}
        </Column>
      </DialogContent>
      <DialogActions>
        <SecondaryButton data-tour="close" type="button" onClick={onClose}>
          Close
        </SecondaryButton>
        <Button
          data-tour="create-interest-charges"
          type="submit"
          disabled={!isValid || isLoading || isCreating}
        >
          Create Interest Charges
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function InterestPreview(props: InterestPreviewProps) {
  const dispatch = useDispatch();
  const { businessIds } =
    useSelector<RootState, InterestPreviewModalContext | null>(
      getContextByModalType(ModalTypes.InterestPreview),
    ) ?? {};
  const initialValues: InterestPreviewFormValues = {
    chargeFromDate: null,
    chargeToDate: formatISO8601DateString(new Date()),
    creditDebit: CreditDebit.BOTH,
  };
  const onSubmit = ({
    chargeFromDate,
    chargeToDate,
    creditDebit,
  }: InterestPreviewFormValues) => {
    const payload = {
      chargeToDate,
      chargeFromDate: chargeFromDate || null,
      creditDebit,
      businessIds,
    };
    // Include the payload as options, they go in the url as query params.
    dispatch(InterestPreviewAction.create(payload, payload));
  };

  return (
    <Formik<InterestPreviewFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={onSubmit}
    >
      <InterestPreviewModal {...props} businessIds={businessIds} />
    </Formik>
  );
}

export default InterestPreview;
